import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import { BUILDER_RULES, BUILDER_TAGS } from '@/courses/values/courses.values';

@Component({
  name: 'ed-subject-builder-rules-list'
})
export default class EdSubjectBuilderRulesListComponent extends Vue {

  @Prop({ default: () => [] }) readonly rulesApplied: any[];

  /* Refs */
  @Ref('dataTable') readonly dataTable: any;

  public removing: boolean = false;

  /* Warning Repeated Rule */
  readonly warningRepeatedRuleClass = 'warning-repeated-rule';
  readonly warningRepeatedRuleRowBorderStyle = '2px #ffbb0063 solid';

  /* Computed */
  get headers() {
    return [
      { text: this.$t('courses.subjectBuilder.rulesContainer.rule'), value: 'name', sortable: false },
      { text: this.$t('common.actions'), value: 'actions', sortable: false },
    ]
  }

  get rulesList() {
    let homework = this.rulesApplied.filter(el => el.label === BUILDER_TAGS.HOMEWORK).length;
    let test = this.rulesApplied.filter(el => el.label === BUILDER_TAGS.TEST).length;
    let classwork = this.rulesApplied.filter(el => el.label === BUILDER_TAGS.CLASSWORK).length;
    let startLesson = this.rulesApplied.filter(el => el.type === BUILDER_RULES.START_LESSON).length;
    return this.rulesApplied.map(el => {
      let isRuleRepeated = (el.label === BUILDER_TAGS.HOMEWORK && homework > 1) 
        || (el.label === BUILDER_TAGS.TEST && test > 1)
        || (el.label === BUILDER_TAGS.CLASSWORK && classwork > 1)
        || (el.type === BUILDER_RULES.START_LESSON && startLesson > 1)
        
      return {
        name: this.getRuleName(el),
        element: el,
        className: isRuleRepeated ? 'warning-repeated-rule' : ''
      }
    })
  }

  get dataTableCustomStyle(): string {
    return 'border-radius: 4px;';
  }

  goToAddRule() {
    this.$emit('toggleAddRules')
  }

  getRuleName(rule) {
    let weekdayT, tag
    switch(rule.type) {
      case BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY:
        weekdayT = this.$t(`common.weekdays.${rule.workingDay.toLowerCase()}`).toString();
        tag = this.$t(`common.tags.${rule.label.toLowerCase()}`);

        return `${tag} ${this.$t('courses.subjectBuilder.rulesContainer.on')} 
          ${weekdayT}${weekdayT.charAt(weekdayT.length-1) !== 's' ? 's' : ''}`
          
      case BUILDER_RULES.START_LESSON:
        return this.$t('courses.subjectBuilder.rulesContainer.startLessonLabel', {
          lessonFrom: rule.startLesson, 
          lessonTo: rule.finalLesson
        });
    }
  }

  getWaringInfo(type) {
    if(type === BUILDER_RULES.START_LESSON)
      return [this.$t('courses.subjectBuilder.rulesContainer.warningStartLessonRepeated'),
        this.$t('courses.subjectBuilder.rulesContainer.willBeApplied') ]
    else 
      return [this.$t('courses.subjectBuilder.rulesContainer.warningTagRepeated'),
        this.$t('courses.subjectBuilder.rulesContainer.willBeApplied') ]
  }

  setUpdateRule(item) {
    this.$emit('onSetUpdateRule', {
      rule: item.element
    });
  }

  removeRule(item) {
    this.$emit('onRemoveRule', {
      rule: item.element
    });
  }

  reRunRule(item) {
    this.$emit('onReRunRule', {
      rule: item.element
    });
  }

  private _loadDataTable(): void {
    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.rulesList || this.rulesList.length === 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }

    const slots = {
      'item.name': {
        template: document.querySelector('#rules\\.item\\.name').innerHTML,
        templateParams: [
          { key: 'item.name', value: (params) => params.item.name ?? '' },
          { key: 'tooltipActiveRule', value: (params) => params.parent.$t('courses.subjectBuilder.rulesContainer.activeRule') ?? '' },
          { key: 'tooltipRepeatedRule',
            value: (params) => params.parent.getWaringInfo(params.item.element.type)[0] + ' ( * ) ' + params.parent.getWaringInfo(params.item.element.type)[1]
          },
        ],
        expressions: [
          {
            target: '.active-rule', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.element.active,
            applied: 'display: inline-flex;', fallback: 'display: none;'
          },
          {
            target: '.repeated-rule', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.className == params.parent.warningRepeatedRuleClass,
            applied: 'display: inline-flex;', fallback: 'display: none;'
          },
        ],
      },
      'item.actions': {
        template: document.querySelector('#rules\\.item\\.actions').innerHTML,
        events: [
          { target: '.run-rule', handler: (params) => params.parent.reRunRule(params.item) },
          { target: '.set-rule', handler: (params) => params.parent.setUpdateRule(params.item) },
          { target: '.remove-rule', handler: (params) => params.parent.removeRule(params.item) },
        ],
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this.rulesList,
      headers: this.headers,
      slots: slots,
    });

    this.$nextTick(() => {
      setTimeout(() => {
        this._validateRulesClasses();
      });
    });
  }

  private _validateRulesClasses(): void {

    const repeatedRulesIndex: string[] = [];
    this.rulesList?.forEach((rule, index) => {
      if (rule.className === this.warningRepeatedRuleClass) {
        repeatedRulesIndex.push(String(index));
      }
    });

    if (!(repeatedRulesIndex.length > 0)) {
      return;
    }

    const tbody = this.dataTable?.shadowRoot?.querySelector('tbody');
    const rows = tbody ? Array.from(tbody?.querySelectorAll('tr')) : [];

    rows.forEach(row => {
      const rowElement = row as HTMLElement;
      if (repeatedRulesIndex.includes(rowElement?.getAttribute('data-index'))) {
        rowElement.style.border = this.warningRepeatedRuleRowBorderStyle;
      }
    });
  }

  @Watch('rulesList', { deep: true, immediate: true })
  private _watchRulesList(): void {
    this.$nextTick(() => {
      this._loadDataTable();
    });
  }
}